import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';

import { displayPartner } from 'Utilities/helpers';
import mediator from 'Utilities/mediator';

import { PARTNER_TYPES } from 'Constants/partnerTypes';

import LibreAppWrapper from 'Containers/LibreAppWrapper';

import ConnectedOn from 'Components/partner/ConnectedOn';
import ContinueSharing from 'Components/partner/ContinueSharing';
import DoneButton from 'Components/partner/DoneButton';
import Header from 'Components/partner/Header';
import ScopeSection from 'Components/partner/ScopeSection';
import SharingDescription from 'Components/partner/SharingDescription';
import StopSharing from 'Components/partner/StopSharing';
import StopSharingButton from 'Components/partner/StopSharingButton';

import styles from './Partner.module.scss';

const mapState = ({
  nav: { location },
  partner: { connected, partners, pendingConnections },
}: RootState) => {
  const id = location.split('/')[2];

  return {
    partner: Object.assign({}, partners[id]),
    partners,
    connected: Object.assign(
      {},
      connected.find((v) => v.clientId === id)
    ),
    pendingConnections: pendingConnections,
    connectedOriginal: connected,
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const Partner: React.FC<React.PropsWithChildren<Props>> = ({
  partner,
  partners,
  connected,
  pendingConnections,
  connectedOriginal,
}) => {
  // We shouldn't be here if this is an invalid partner, or the partner shouldn't be displayed
  useEffect(() => {
    if (
      !partner.id ||
      (partner.type === PARTNER_TYPES.INBOUND && !connected.clientId) ||
      (partner.type !== PARTNER_TYPES.REDOX && !displayPartner(partner) && !connected.clientId)
    ) {
      mediator.publish('partner:navigateOtherConnections', {
        partners,
        connected: connectedOriginal,
        pending: pendingConnections,
      });
    }
  }, [partner, partners, connected.clientId, connectedOriginal, pendingConnections]);

  const getScopes = (type: string) => {
    return (connected.scopes && connected.scopes.filter((scope) => scope.startsWith(type))) || [];
  };

  if (!partner.id) {
    return null;
  }

  const showInstructions = partner.type !== PARTNER_TYPES.INBOUND;

  return (
    <LibreAppWrapper app={partner} appType="partner">
      <Header />

      <div id="info-list" className={styles.infoList}>
        <SharingDescription partner={partner} scopes={connected.scopes} />

        {/* Read and write permissions */}

        <ScopeSection
          partner={partner}
          scopes={getScopes('read')}
          scopeType="read"
          scopeDetails={connected.scopeDetails}
        />

        <ScopeSection
          partner={partner}
          scopes={getScopes('write')}
          scopeType="write"
          scopeDetails={connected.scopeDetails}
        />

        {/* Connected on */}
        <div id="connected-on-info" className="pt-3">
          <ConnectedOn connected={connected} />
          {showInstructions ? <StopSharing partner={partner} /> : null}
        </div>
      </div>
      {/* Done */}
      {showInstructions ? <ContinueSharing /> : null}
      <StopSharingButton partner={partner} />
      <DoneButton
        partners={partners}
        connected={connectedOriginal}
        pendingConnections={pendingConnections}
      />
    </LibreAppWrapper>
  );
};

export default connector(Partner);
